import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceDot,
} from 'recharts';
import { ComponentTypeEnum, ComponentResult } from '../../types';

interface FlowPressureChartProps {
  data: Record<string, number>[];
  componentType: ComponentTypeEnum;
  simulationResult?: ComponentResult;
}

export const FlowPressureChart: React.FC<FlowPressureChartProps> = ({
  data,
  componentType,
  simulationResult,
}) => {
  const isPump = componentType === 'pump';
  const pressureKey = isPump ? 'pressure_rise' : 'pressure_drop';
  const yLabel = isPump ? 'Pressure Rise [kPa]' : 'Pressure Drop [kPa]';

  const sortedData = useMemo(
    () => [...data].sort((a, b) => (a.flow_rate ?? 0) - (b.flow_rate ?? 0)),
    [data]
  );

  const operatingPoint = useMemo(() => {
    if (!simulationResult) return null;
    const flowRate = simulationResult.flow_rate;
    const pressure = isPump
      ? -(simulationResult.pressure_drop ?? 0) // pump pressure_drop is negative; curve shows positive rise
      : (simulationResult.pressure_drop ?? 0);
    return { flow_rate: flowRate, [pressureKey]: pressure };
  }, [simulationResult, isPump, pressureKey]);

  return (
    <ResponsiveContainer width="100%" height={180}>
      <LineChart data={sortedData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid stroke="#f1f3f4" strokeDasharray="4 4" />
        <XAxis
          dataKey="flow_rate"
          tick={{ fontSize: 10, fill: '#5f6368' }}
          label={{ value: 'Flow [l/min]', position: 'insideBottom', offset: -2, fontSize: 10, fill: '#5f6368' }}
        />
        <YAxis
          dataKey={pressureKey}
          tick={{ fontSize: 10, fill: '#5f6368' }}
          label={{ value: yLabel, angle: -90, position: 'insideLeft', offset: 10, fontSize: 10, fill: '#5f6368' }}
          width={50}
        />
        <Tooltip
          contentStyle={{ fontSize: 11, maxWidth: 200 }}
          formatter={(value: any) => [Number(value).toFixed(2), yLabel.split(' [')[0]]}
          labelFormatter={(label: any) => `Flow: ${label} l/min`}
        />
        <Line
          type="monotone"
          dataKey={pressureKey}
          stroke="#1a73e8"
          strokeWidth={1.5}
          dot={false}
          activeDot={{ r: 3 }}
        />
        {operatingPoint && (
          <ReferenceDot
            x={operatingPoint.flow_rate}
            y={operatingPoint[pressureKey] as number}
            r={5}
            fill="#d93025"
            stroke="#ffffff"
            strokeWidth={2}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};
